/**
 * Single source of truth for the Club App event catalog.
 *
 * Order matters — Browse Events renders cards top-to-bottom in this order.
 * The order below mirrors how members think about events:
 *   1. Tournaments              (knockout events — top draw)
 *   2. Singles ladders          (always-open, both genders)
 *   3. Court Movement nights    (Men's / Ladies Night)
 *   4. Doubles / round robins   (coming soon)
 *
 * Status values:
 *   - 'active'      → live event the user can use today
 *   - 'coming_soon' → visible but inert. No route, no click handler.
 *
 * `route` is the destination when status === 'active'. Coming-soon entries
 * omit it on purpose so any accidental Link render becomes a no-op instead
 * of a broken redirect.
 */

export const EVENTS = [
  // ---------- Tournaments ----------
  {
    slug: 'tournaments',
    name: 'Tournaments',
    type: 'tournament',
    gender: null,
    status: 'active',
    icon: 'Trophy',
    blurb: 'Singles and doubles brackets — admin-managed knockout tournaments.',
    route: '/tournaments',
  },

  // ---------- Always-open ladders ----------
  {
    slug: 'singles-ladder',
    name: 'Men\u2019s Singles Ladder',
    type: 'singles_ladder',
    gender: 'mens',
    status: 'active',
    always_open: true,
    accent: '#015e3f',
    icon: 'ListNumbers',
    blurb: 'View standings, rankings, streaks, and player profiles. Join the ladder to start challenging other players.',
    route: '/ladder',
  },
  {
    slug: 'ladies-singles-ladder',
    name: 'Ladies Singles Ladder',
    type: 'singles_ladder',
    gender: 'womens',
    status: 'active',
    always_open: true,
    icon: 'ListNumbers',
    blurb: 'Challenge-based singles ladder for women. Climb the rankings one match at a time.',
    route: '/ladder/ladies-singles-ladder',
  },

  // ---------- Court Movement nights ----------
  {
    slug: 'mens-night',
    name: "Men's Night",
    type: 'social_night',
    gender: 'mens',
    status: 'active',
    icon: 'TennisBall',
    // Court Movement League is live. Route into the Events tab "Open"
    // filter so users land on registrations they can actually act on
    // (falls back to other tabs if Open is empty — EventsPage handles UX).
    route: '/events?tab=open',
    blurb: 'Weekly court-movement night. Register for the next session or browse current standings.',
  },
  {
    slug: 'ladies-night',
    name: 'Ladies Night',
    type: 'social_night',
    gender: 'womens',
    status: 'active',
    icon: 'TennisBall',
    route: '/events?tab=open',
    blurb: 'Weekly court-movement night for women. Register for the next session or browse current standings.',
  },

  // ---------- Doubles ladders (coming soon) ----------
  {
    slug: 'mens-doubles-ladder',
    name: "Men's Doubles Ladder",
    type: 'doubles_ladder',
    gender: 'mens',
    status: 'coming_soon',
    icon: 'UsersFour',
    blurb: 'Two-player team ladder. Same challenge format, doubled up.',
  },
  {
    slug: 'womens-doubles-ladder',
    name: "Women's Doubles Ladder",
    type: 'doubles_ladder',
    gender: 'womens',
    status: 'coming_soon',
    icon: 'UsersFour',
    blurb: 'Two-player team ladder for women.',
  },
  {
    slug: 'mixed-doubles-ladder',
    name: 'Mixed Doubles Ladder',
    type: 'doubles_ladder',
    gender: 'mixed',
    status: 'coming_soon',
    icon: 'UsersFour',
    blurb: 'Mixed teams of one man + one woman.',
  },

  // ---------- Round Robins / future event types ----------
  {
    slug: 'round-robins',
    name: 'Round Robins',
    type: 'round_robin',
    gender: null,
    status: 'coming_soon',
    icon: 'ArrowsClockwise',
    blurb: 'Drop-in events where every player faces every other player.',
  },
];

// Always-open ladder slugs used by the Events page Open/In-Progress tabs to
// inject "ladder is always running" rows next to live sessions/tournaments.
export const ALWAYS_OPEN_LADDERS = EVENTS.filter((e) => e.always_open);

export function findEventBySlug(slug) {
  return EVENTS.find((e) => e.slug === slug) || null;
}

export const EVENT_STATUS_LABEL = {
  active: 'Open',
  coming_soon: 'Coming soon',
};
